import { useState } from 'react';

import { IconChevronRight, IconExclamationCircle } from '@tabler/icons-react';
import { AnimatePresence, motion } from 'motion/react';

import { Accordion } from '@cfa/react-core';

import { cn } from '@/utils/tailwind';

import { Shimmer, type TextShimmerProps } from '../../../shimmer';

interface CollapsibleContentProps
  extends React.PropsWithChildren, Omit<TextShimmerProps, 'children'> {
  /** The title of the collapsible content */
  title?: string;
  /** Whether to shimmer the title @default false */
  shimmer?: boolean;
  accordionItemClassName?: string;
  accordionTriggerClassName?: string;
  accordionPanelClassName?: string;
  /** Whether to show the error icon @default false */
  error?: boolean;
}

const CollapsibleContent: React.FC<CollapsibleContentProps> = ({
  title,
  children,
  shimmer = false,
  accordionItemClassName,
  accordionTriggerClassName,
  accordionPanelClassName,
  className,
  error = false,
  ...textShimmerProps
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Accordion.Root>
      <Accordion.Item
        isExpanded={isOpen}
        onExpandedChange={setIsOpen}
        className={cn(
          'w-fit! border-none! shadow-none!',
          accordionItemClassName,
        )}
      >
        <Accordion.Trigger
          className={cn(
            'w-fit! gap-1! p-0! [&_svg:not(#chevron-right):not(#x)]:hidden!',

            accordionTriggerClassName,
          )}
        >
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="group/collapsible-content group flex items-center gap-1"
          >
            {error && (
              <IconExclamationCircle
                size={16}
                id="x"
                className="text-feedback-error-base"
              />
            )}
            {shimmer && title ? (
              <Shimmer
                className={cn('text-sm', className)}
                {...textShimmerProps}
              >
                {title}
              </Shimmer>
            ) : (
              <p
                className={cn(
                  'text-variant-body2 text-gray-500! transition-colors group-hover/collapsible-content:text-gray-800!',
                  className,
                )}
              >
                {title}
              </p>
            )}
            <IconChevronRight
              size={16}
              id="chevron-right"
              className={cn(
                'text-gray-500! transition-all group-hover/collapsible-content:text-gray-800!',
                {
                  'rotate-90': isOpen,
                },
              )}
            />
          </motion.div>
        </Accordion.Trigger>
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{
                height: { duration: 0.15, ease: 'easeInOut' },
                opacity: { duration: 0.12, ease: 'easeInOut' },
              }}
              style={{ overflow: 'hidden' }}
              className={cn('p-0!', accordionPanelClassName)}
            >
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </Accordion.Item>
    </Accordion.Root>
  );
};

export default CollapsibleContent;
